import { useLanguage } from "../i18n";
import { useLogger } from "../logger";
import { useReasonText } from "../reasons";
import { photoFor, productPhotoFor } from "../photos";
import type { Blocked } from "../tasksApi";
import type { Specimen } from "../types";

/**
 * The feedings the app is deliberately not asking for, each with the rule that
 * stopped it.
 *
 * The explanation is the useful part — an app that simply stays quiet teaches
 * nothing, and the owner's whole guide exists because intervals alone are not
 * sufficient. These rules govern what the app *asks* for, never what it will
 * accept, so the override sits right beside the refusal.
 *
 * A withheld feeding is drawn as an outline in the heat colour rather than a
 * fill: the same channel as "late", but nothing here is being asked of him.
 */
export function NotAsking({
  blocked,
  plants,
}: {
  blocked: Blocked[];
  plants: Map<string, Specimen>;
}) {
  const { t } = useLanguage();
  const { log } = useLogger();
  const say = useReasonText();

  if (blocked.length === 0) return null;

  return (
    <section className="not-asking">
      <h2>{t.notFeedingToday}</h2>
      <p className="not-asking-lede">{t.notFeedingLede}</p>

      <ul className="refusals">
        {blocked.map((entry) => {
          const plant = plants.get(entry.specimen_id);
          const name = plant?.species.name.ar ?? "—";
          const photo = photoFor(plant?.species.scientific_name);
          const label = productPhotoFor(entry.product);
          return (
            <li key={`${entry.specimen_id}-${entry.product}`} className="refusal">
              <div className="refusal-head">
                {photo && <img className="refusal-photo" src={photo} alt="" loading="lazy" />}
                <div className="refusal-names">
                  <span className="refusal-plant" lang="ar">
                    {name}
                  </span>
                  <span className="refusal-en" dir="ltr">
                    {plant?.species.name.en}
                  </span>
                </div>
              </div>

              {/* Struck through, not hidden: he can see what was skipped. */}
              <p className="refusal-product">
                {label && <img src={label} alt="" loading="lazy" />}
                <span dir="ltr">
                  {entry.product}
                  {entry.dose ? ` — ${entry.dose}` : ""}
                </span>
              </p>

              <p className="refusal-rule">{say(entry.reason)}</p>

              {entry.until && (
                <p className="refusal-until">
                  {t.clearOn}{" "}
                  {new Date(entry.until).toLocaleDateString(undefined, {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })}
                </p>
              )}

              {/* The override. It writes a normal Care Event carrying its
                  product, so the engine absorbs it like any other feeding. */}
              <div className="chips">
                <button
                  type="button"
                  className="chip"
                  onClick={() =>
                    log(
                      entry.specimen_id,
                      "feed",
                      { product_id: entry.product_id, dose: entry.dose, overridden: true },
                      `${name} — ${entry.product}`,
                    )
                  }
                >
                  {t.feedAnyway}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
